import React, { useState, useEffect } from 'react';
import { Filter, Search, MapPin, Briefcase, RefreshCw } from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import api from '../utils/api';
import { JobCard } from '../components/JobCard';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { CTASection } from '../components/CTASection';
import { ResourceButtons } from '../components/ResourceButtons';
import { getUser } from '../utils/auth';
import { toast } from 'sonner';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';

export default function Jobs() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isEmbed = searchParams.get('embed') === 'true';
  
  const [allJobs, setAllJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savedJobs, setSavedJobs] = useState([]);
  const user = getUser();
  
  const [filters, setFilters] = useState({
    search: '',
    location: '',
    job_type: '',
    experience_level: '',
    category: ''
  });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const search = params.get('search') || '';
    const category = params.get('category') || '';
    const locationParam = params.get('location') || '';
    setFilters(prev => ({ ...prev, search, category, location: locationParam }));
  }, [location.search]);

  useEffect(() => {
    fetchAllJobs();
    if (user && user.role === 'job_seeker') {
      fetchSavedJobs();
    }
  }, [filters]);

  const fetchAllJobs = async () => {
    try {
      setLoading(true);
      
      // Build query params for internal jobs
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });
      
      // Fetch both our jobs and Zoho jobs
      const [ourJobsRes, zohoJobsRes] = await Promise.all([
        api.get(`/jobs?${params.toString()}`),
        api.get(`/zoho/jobs?${params.toString()}`).catch(() => ({ data: [] }))
      ]);
      
      // Mark jobs with their source
      const ourJobs = ourJobsRes.data.map(job => ({ ...job, source: 'internal' }));
      const zohoJobs = zohoJobsRes.data.map(job => ({ ...job, source: 'zoho' }));
      
      // Combine with Zoho jobs FIRST, then internal jobs
      setAllJobs([...zohoJobs, ...ourJobs]);
    } catch (error) {
      console.error('Failed to fetch jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSavedJobs = async () => {
    try {
      const response = await api.get('/saved-jobs');
      setSavedJobs(response.data.map(job => job.id));
    } catch (error) {
      console.error('Failed to fetch saved jobs:', error);
    }
  };

  const handleSaveJob = async (jobId) => {
    if (!user || user.role !== 'job_seeker') {
      toast.error('Please login as a job seeker to save jobs');
      return;
    }

    try {
      if (savedJobs.includes(jobId)) {
        await api.delete(`/saved-jobs/${jobId}`);
        setSavedJobs(savedJobs.filter(id => id !== jobId));
        toast.success('Job removed from saved');
      } else {
        await api.post(`/saved-jobs/${jobId}`);
        setSavedJobs([...savedJobs, jobId]);
        toast.success('Job saved successfully');
      }
    } catch (error) {
      toast.error('Failed to save job');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAllJobs();
  };

  const handleJobClick = (job) => {
    const embedParam = isEmbed ? '?embed=true' : '';
    if (job.source === 'zoho') {
      navigate(`/jobs/zoho/${job.id}${embedParam}`);
    } else {
      navigate(`/jobs/${job.id}${embedParam}`);
    }
  };

  const formatSalary = (job) => {
    if (job.salary) return job.salary;
    
    const min = job.salary_min || 0;
    const max = job.salary_max || 0;
    
    if (min === 0 && max === 0) return 'Negotiable';
    
    const salary = max || min;
    
    if (salary < 1000) {
      const formatHourly = (val) => {
        const formatted = val.toFixed(2);
        return formatted.endsWith('.00') ? val.toString() : formatted;
      };
      if (min > 0 && max > 0 && min !== max) {
        return `$${formatHourly(min)}-$${formatHourly(max)}/hr`;
      }
      return `$${formatHourly(salary)}/hr`;
    }
    
    if (min > 0 && max > 0 && min !== max) {
      return `$${(min / 1000).toFixed(0)}k - $${(max / 1000).toFixed(0)}k/yr`;
    }
    return `$${(salary / 1000).toFixed(0)}k/yr`;
  };

  const truncateDescription = (desc, maxLength = 180) => {
    if (!desc) return 'No description available';
    const stripped = desc.replace(/<[^>]*>/g, '');
    if (stripped.length <= maxLength) return stripped;
    return stripped.substring(0, maxLength) + '...';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-50 to-cyan-50/30" data-testid="jobs-page">
      <Helmet>
        <title>Browse Jobs | Can X Global Careers</title>
        <meta name="description" content={`Browse ${allJobs.length} current job openings in Canada. Find full-time, part-time, and contract positions across technology, healthcare, engineering, and more.`} />
        <meta property="og:title" content="Browse Jobs | Can X Global Careers" />
        <meta property="og:description" content="Find your next career opportunity. Browse hundreds of job listings from top Canadian employers." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://canxglobal.com/jobs" />
      </Helmet>
      
      {/* Hero Section - Hidden in embed mode */}
      {!isEmbed && (
        <section className="hero-gradient py-12 md:py-20 px-4 md:px-6">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-black mb-4 md:mb-6" data-testid="jobs-heading">
              Find Your Next <span className="text-transparent bg-clip-text bg-gradient-to-r from-brand-gradient-start to-brand-gradient-end">Opportunity</span>
            </h1>
            <p className="text-base md:text-lg text-zinc-600 max-w-2xl mx-auto">
              Browse {allJobs.length} current job openings
            </p>
          </div>
        </section>
      )}

      <div className="max-w-7xl mx-auto px-4 md:px-6 -mt-6 md:-mt-8 pb-12 md:pb-20">
        {/* Search and Filters */}
        <div className="glass-morphism rounded-2xl md:rounded-3xl p-4 md:p-8 shadow-xl md:shadow-2xl mb-8 md:mb-12" data-testid="search-filters-section">
          <form onSubmit={handleSearch} className="space-y-4 md:space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
              <div className="lg:col-span-2">
                <div className="relative group">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 group-focus-within:text-brand-blue transition-colors" />
                  <Input
                    type="text"
                    placeholder="Job title, keywords, or company"
                    className="h-12 md:h-14 rounded-xl md:rounded-2xl pl-12 bg-white/80 hover:bg-white focus:bg-white transition-all border-cyan-100 text-sm md:text-base"
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    data-testid="search-input"
                  />
                </div>
              </div>

              <div className="relative group">
                <MapPin className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400 group-focus-within:text-brand-blue transition-colors" />
                <Input
                  type="text"
                  placeholder="Location"
                  className="h-12 md:h-14 rounded-xl md:rounded-2xl pl-12 bg-white/80 hover:bg-white focus:bg-white transition-all border-cyan-100 text-sm md:text-base"
                  value={filters.location}
                  onChange={(e) => setFilters({ ...filters, location: e.target.value })}
                  data-testid="location-input"
                />
              </div>

              <div>
                <select
                  className="w-full h-12 md:h-14 rounded-xl md:rounded-2xl border border-cyan-100 px-4 bg-white/80 hover:bg-white focus:bg-white transition-all focus-visible:ring-2 focus-visible:ring-brand-blue outline-none text-sm md:text-base"
                  value={filters.job_type}
                  onChange={(e) => setFilters({ ...filters, job_type: e.target.value })}
                  data-testid="job-type-select"
                >
                  <option value="">All Job Types</option>
                  <option value="full-time">Full Time</option>
                  <option value="part-time">Part Time</option>
                  <option value="contract">Contract</option>
                  <option value="remote">Remote</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              <div>
                <select
                  className="w-full h-12 md:h-14 rounded-xl md:rounded-2xl border border-cyan-100 px-4 bg-white/80 hover:bg-white focus:bg-white transition-all focus-visible:ring-2 focus-visible:ring-brand-blue outline-none text-sm md:text-base"
                  value={filters.experience_level}
                  onChange={(e) => setFilters({ ...filters, experience_level: e.target.value })}
                  data-testid="experience-select"
                >
                  <option value="">Experience Level</option>
                  <option value="entry">Entry Level</option>
                  <option value="mid">Mid Level</option>
                  <option value="senior">Senior Level</option>
                </select>
              </div>

              <div>
                <select
                  className="w-full h-12 md:h-14 rounded-xl md:rounded-2xl border border-cyan-100 px-4 bg-white/80 hover:bg-white focus:bg-white transition-all focus-visible:ring-2 focus-visible:ring-brand-blue outline-none text-sm md:text-base"
                  value={filters.category}
                  onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                  data-testid="category-select"
                >
                  <option value="">All Categories</option>
                  <option value="Technology">Technology</option>
                  <option value="Healthcare">Healthcare</option>
                  <option value="Engineering">Engineering</option>
                  <option value="Trades">Trades</option>
                  <option value="Food Service">Food Service</option>
                  <option value="Sales">Sales</option>
                  <option value="Transportation">Transportation</option>
                  <option value="Landscaping">Landscaping</option>
                </select>
              </div>

              <div className="col-span-2 md:col-span-1">
                <Button 
                  type="submit" 
                  className="w-full h-12 md:h-14 bg-gradient-to-r from-brand-gradient-start to-brand-gradient-end text-white font-bold rounded-xl md:rounded-2xl hover:shadow-2xl hover:scale-105 transition-all text-sm md:text-base" 
                  data-testid="apply-filters-btn"
                >
                  <Filter className="w-4 h-4 md:w-5 md:h-5 mr-2" />
                  Apply Filters
                </Button>
              </div>
            </div>
          </form>
        </div>

        {/* Job Listings */}
        {loading ? (
          <div className="text-center py-16 md:py-20 glass-morphism rounded-2xl md:rounded-3xl" data-testid="loading-state">
            <RefreshCw className="w-10 h-10 text-zinc-400 mx-auto mb-4 animate-spin" />
            <div className="text-base md:text-lg text-zinc-500 font-medium">Loading jobs...</div>
          </div>
        ) : allJobs.length === 0 ? (
          <div className="text-center py-16 md:py-20 glass-morphism rounded-2xl md:rounded-3xl" data-testid="no-jobs-state">
            <Briefcase className="w-16 h-16 text-zinc-300 mx-auto mb-4" />
            <div className="text-base md:text-lg text-zinc-500 font-medium">No jobs found. Try adjusting your filters.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-4 md:gap-6" data-testid="jobs-list">
            {allJobs.map((job) => (
              job.source === 'internal' ? (
                <JobCard
                  key={job.id}
                  job={job}
                  onSave={user && user.role === 'job_seeker' ? handleSaveJob : null}
                  isSaved={savedJobs.includes(job.id)}
                />
              ) : (
                <div
                  key={job.id}
                  onClick={() => handleJobClick(job)}
                  className="bg-white rounded-2xl p-5 md:p-6 shadow-sm hover:shadow-lg transition-all cursor-pointer border border-zinc-100 hover:border-cyan-200"
                  data-testid={`job-card-${job.id}`}
                >
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex-1">
                      {/* Location */}
                      <div className="flex items-center text-zinc-500 text-sm mb-2">
                        <MapPin className="w-4 h-4 mr-1" />
                        <span>{job.location || 'Location not specified'}</span>
                      </div>

                      {/* Title */}
                      <h3 className="text-lg md:text-xl font-bold text-zinc-900 mb-2 hover:text-brand-blue transition-colors">
                        {job.title}
                      </h3>

                      {/* Company */}
                      <p className="text-zinc-600 font-medium mb-2">{job.company}</p>

                      {/* Salary */}
                      <p className="text-brand-blue font-semibold mb-3" data-testid="job-salary">
                        {formatSalary(job)}
                      </p>

                      {/* Description Preview */}
                      <p className="text-zinc-500 text-sm leading-relaxed mb-4">
                        {truncateDescription(job.description)}
                      </p>

                      {/* Tags */}
                      <div className="flex flex-wrap gap-2">
                        <span className="px-3 py-1 bg-cyan-50 text-cyan-700 rounded-full text-xs font-medium capitalize">
                          {job.job_type?.replace('-', ' ') || 'Full-Time'}
                        </span>
                        {job.remote_job && (
                          <span className="px-3 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                            Remote
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        )}
      </div>
      
      {/* CTA Section */}
      <CTASection />
      
      {/* Resource Section */}
      <ResourceButtons />
    </div>
  );
}
